import { Head, Link } from '@inertiajs/react';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import type { AdminSponsorsShow } from '@/types';
import { CreateSponsorCreditDialog } from './components/CreateSponsorCreditDialog';
import { SponsorCreditsSearch } from './components/SponsorCreditsSearch';
import { SponsorCreditsTable } from './components/SponsorCreditsTable';

export default function Show({
  sponsor,
  credits,
  hackerOptions,
  eventSlug,
  search,
  title,
}: AdminSponsorsShow) {
  return (
    <>
      <Head title={title} />
      <div className="container mx-auto space-y-6 py-8">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link href={`/admin/${eventSlug}/sponsors`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to sponsors
            </Link>
          </Button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-bold text-3xl tracking-tight">
              {sponsor.name}
            </h1>
            <div className="flex items-center gap-3 text-muted-foreground text-sm">
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                {sponsor.slug}
              </code>
              {sponsor.url && (
                <a
                  href={sponsor.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                >
                  <ExternalLink className="h-3 w-3" />
                  {sponsor.url}
                </a>
              )}
            </div>
          </div>
          <CreateSponsorCreditDialog
            sponsorId={sponsor.id}
            sponsorSlug={sponsor.slug}
            eventSlug={eventSlug}
            hackerOptions={hackerOptions}
          />
        </div>

        <Card>
          <CardHeader>
            <SponsorCreditsSearch
              eventSlug={eventSlug}
              sponsorSlug={sponsor.slug}
              search={search}
            />
            <p className="text-muted-foreground text-sm">
              {credits.length} {credits.length === 1 ? 'credit' : 'credits'}{' '}
              found
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <SponsorCreditsTable
              sponsorId={sponsor.id}
              sponsorSlug={sponsor.slug}
              eventSlug={eventSlug}
              credits={credits}
              hackerOptions={hackerOptions}
            />
          </CardContent>
        </Card>
      </div>
    </>
  );
}
